import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import RestHttpClient from '../../../common/RestHttpClient';
import {
  FeatureValueView,
  UpdateFeatureValueRequest,
  ClassificationType,
} from '../../../generated/ApiClient';
import { useState } from 'react';
import { ObjectFeatureDialog } from './ObjectFeatureDialog';
import { FeatureValueList } from '../components/FeatureValueList';
import { RuleSetList } from './RuleSetList';

export function ObjectDetail() {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<FeatureValueView | null>(null);
  const [classificationType, setClassificationType] = useState<ClassificationType>();
  const [domainId, setDomainId] = useState<number>();

  const classificationTypeOptions = Object.values(ClassificationType).map((type) => ({
    label: t(`classification.classifiedObject.types.${type}`),
    value: type,
  }));

  const {
    data = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['classification', 'objects', domainId, classificationType],
    queryFn: () => RestHttpClient.getFeatureValues(domainId!, classificationType!),
    select: (x) => x.data,
    enabled: !!domainId && !!classificationType,
  });

  const handleRowClick = (e: React.MouseEvent<unknown>, id: string) => {
    e.stopPropagation();

    const feature = data.find((x) => x.featureId === id);

    if (feature) {
      setSelectedFeature(feature);
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedFeature(null);
    refetch();
  };

  const onFeatureValueUpdate = async (data: {
    id: string;
    name: string;
    featureType: any;
    value: any;
  }) => {
    if (!domainId || !classificationType) return;

    await RestHttpClient.updateFeatureValue(
      new UpdateFeatureValueRequest({
        domainId: domainId,
        objectType: classificationType,
        featureId: data.id,
        value: data.value,
      })
    );

    handleDialogClose();
  };

  const handleAddFeature = () => {
    setSelectedFeature(null);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
        <FormControl variant="standard" sx={{ minWidth: 200 }}>
          <InputLabel>{t('classification.classifiedObject.type')}</InputLabel>
          <Select
            value={classificationType || ''}
            onChange={(e) => {
              setClassificationType(e.target.value as ClassificationType);
            }}
          >
            {classificationTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={t('classification.classifiedObject.domainId')}
          type="number"
          variant="standard"
          value={domainId || ''}
          onChange={(e) => {
            const value = e.target.value;
            setDomainId(Number(value));
          }}
          sx={{ minWidth: 150 }}
        />
      </Box>

      {classificationType && domainId && (
        <>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}
          >
            <Typography variant="h5">
              {t('classification.object.details', {
                type: classificationType,
                id: domainId,
              })}
            </Typography>
            <Button variant="contained" onClick={handleAddFeature}>
              {t('classification.features.addFeature')}
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ width: '100%', mb: 2 }}>
                <FeatureValueList
                  featureValues={data}
                  isLoading={isLoading}
                  handleRowClick={handleRowClick}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* <RuleSetList domainId={domainId} classificationType={classificationType} /> */}
            </Grid>
          </Grid>
        </>
      )}

      <ObjectFeatureDialog
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={onFeatureValueUpdate}
        feature={
          selectedFeature
            ? {
                id: selectedFeature.featureId,
                name: selectedFeature.name,
                featureType: selectedFeature.featureType,
                value: selectedFeature.value,
              }
            : null
        }
      />
    </Box>
  );
}
